import { gsap } from 'gsap';
import { BaseEffectModule } from '../BaseEffectModule.js';

/**
 * Basic Animations Module - FIXED VERSION (No Position Conflicts + BLUR FIX)
 * ✅ Never touches x,y when parallax is active
 * ✅ FIXED: Blur animations are now smooth (no jumps from blur(X) to 'none')
 */
export class BasicAnimationsModule extends BaseEffectModule {
    constructor() {
        super('basicAnimations', {
            version: '1.0.3', // ← Zwiększona wersja po blur fix
            description: 'Core animation properties - FIXED (no position conflicts + smooth blur)'
        });
        
        this.propertyDefaults = {
            opacity: 1,
            x: 0,
            y: 0,
            rotation: 0,
            scale: 1,
            blur: 0,
            skewX: 0,
            skewY: 0,
            borderRadius: 0,
            clipProgress: 100,
            backgroundColor: 'transparent',
            textColor: 'inherit'
        };
    }
    
    createState(element, config) {
        const basicConfig = config.basicAnimations;
        
        // ✅ CRITICAL: Check if parallax is active on this element
        const hasParallax = element.classList.contains('unified-scroll-parallax') || 
                           element.classList.contains('unified-mouse-parallax');
        
        const initialProps = this.prepareAnimationProps(basicConfig.initialState, hasParallax);
        const finalProps = this.prepareAnimationProps(basicConfig.finalState, hasParallax);

        // ✅ CRITICAL: Never set x,y if parallax is active
        if (!hasParallax) {
            gsap.set(element, initialProps);
        } else {
            // Set only non-position properties
            const nonPositionProps = { ...initialProps };
            delete nonPositionProps.x;
            delete nonPositionProps.y;
            gsap.set(element, nonPositionProps);
            
            // console.log(`🚫 BasicAnimations excluding x,y for parallax element: ${element.id}`);
        }
        
        const state = {
            config: basicConfig,
            initialProps: initialProps,
            finalProps: finalProps,
            hasParallax: hasParallax, // ✅ CRITICAL: Remember parallax state
            currentProgress: 0,
            targetProgress: 0,
            lastProgress: 0,
            smoothing: 0.1,
            isAnimating: false,
            needsUpdate: true,
            isScrubMode: false,
            scrubSmoothness: 0.15
        };
        
        // console.log(`✨ BasicAnimations created${hasParallax ? ' (parallax-aware)' : ''}: ${element.id}`);
        return state;
    }
    
    update(element, state, sharedState, deltaTime, cache) {
        if (!this.isEnabled || !state.needsUpdate) return;
        
        const targetSmoothness = state.isScrubMode ? state.scrubSmoothness : state.smoothing;
        const hasProgressChange = this.isSignificantChange(state.currentProgress, state.targetProgress);
        
        if (hasProgressChange) {
            state.currentProgress += (state.targetProgress - state.currentProgress) * targetSmoothness;
            
            if (Math.abs(state.targetProgress - state.currentProgress) < 0.001) {
                state.currentProgress = state.targetProgress;
                state.needsUpdate = false;
            }
            
            // ✅ CRITICAL: Apply interpolated props excluding position if parallax active
            this.applyInterpolatedProps(element, state);
            state.lastProgress = state.currentProgress;
        }
    }
    
    applyInterpolatedProps(element, state) {
        const interpolatedProps = this.interpolateProps(
            state.initialProps,
            state.finalProps,
            state.currentProgress
        );
        
        // ✅ CRITICAL: Never touch x,y if parallax is active
        if (state.hasParallax) {
            delete interpolatedProps.x;
            delete interpolatedProps.y;
        }
        
        // ✅ ONLY APPLY IF HAS PROPERTIES
        if (Object.keys(interpolatedProps).length > 0) {
            gsap.set(element, interpolatedProps);
        }
    }
    
    /**
     * ✅ FIXED: Prepare props with parallax awareness
     */
    prepareAnimationProps(rawProps, hasParallax = false) {
        const props = {};
        
        // ✅ CRITICAL: Skip position properties if parallax is active
        const allowedProps = hasParallax ? 
            ['opacity', 'rotation', 'scale', 'skewX', 'skewY'] : 
            ['opacity', 'x', 'y', 'rotation', 'scale', 'skewX', 'skewY'];
        
        allowedProps.forEach(prop => {
            if (rawProps[prop] !== undefined) {
                props[prop] = rawProps[prop];
            }
        });
        
        // Advanced properties (never position-related)
        this.handleAdvancedProperties(rawProps, props);
        
        return props;
    }
    
    /**
     * ✅ BLUR FIX: Handle advanced properties with smooth blur support
     */
    handleAdvancedProperties(rawProps, props) {
        // ✅ BLUR FIX: Always use blur(Xpx) format, never 'none'
        if (rawProps.blur !== undefined) {
            // ❌ OLD CODE: Caused jumps from blur(10px) to 'none'
            // if (rawProps.blur > 0) {
            //     props.filter = `blur(${rawProps.blur}px)`;
            // } else {
            //     props.filter = 'none'; // ← PROBLEM: GSAP can't smoothly animate to 'none'
            // }
            
            // ✅ NEW CODE: Always blur(Xpx) for smooth GSAP animation
            props.filter = `blur(${rawProps.blur}px)`; // Even blur(0px) works smoothly!
        }
        
        // Border radius
        if (rawProps.borderRadius !== undefined) {
            props.borderRadius = `${rawProps.borderRadius}px`;
        }
        
        // Background color
        if (rawProps.backgroundColor && rawProps.backgroundColor !== 'transparent') {
            props.backgroundColor = rawProps.backgroundColor;
        }
        
        // Text color
        if (rawProps.textColor && rawProps.textColor !== 'inherit') {
            props.color = rawProps.textColor;
        }
        
        // Clip path with progress
        if (rawProps.clipPath) {
            const progress = rawProps.clipProgress !== undefined ? rawProps.clipProgress : 100;
            const inverse = 100 - progress;
            
            let clipValue = rawProps.clipPath;
            clipValue = clipValue.replace(/{progress}/g, progress);
            clipValue = clipValue.replace(/{inverse}/g, inverse);
            
            props.clipPath = clipValue;
        }
    }
    
    /**
     * ✅ FIXED: setProgress with parallax awareness
     */
    setProgress(state, progress) {
        const clampedProgress = this.clamp(progress, 0, 1);
        if (this.isSignificantChange(state.targetProgress, clampedProgress)) {
            state.targetProgress = clampedProgress;
            state.needsUpdate = true;
            state.isScrubMode = true;
            
            if (state.hasParallax) {
                // console.log(`🎯 BasicAnimations setProgress (parallax-aware): ${clampedProgress.toFixed(3)}`);
            }
        }
    }
    
    /**
     * ✅ FIXED: animateToFinal with parallax awareness
     */
    animateToFinal(element, state) {
        state.isAnimating = true;
        state.isScrubMode = false;

        // ✅ CRITICAL: Exclude position properties if parallax is active
        const animationProps = { ...state.finalProps };
        if (state.hasParallax) {
            delete animationProps.x;
            delete animationProps.y;
        }
        
        const timeline = gsap.timeline({
            onComplete: () => {
                state.isAnimating = false;
                state.currentProgress = 1;
                state.targetProgress = 1;
                state.needsUpdate = false;
            }
        });
        
        const finalProps = {
            ...animationProps,
            duration: state.config.timing.duration,
            delay: state.config.timing.delay,
            ease: state.config.timing.ease
        };
        
        if (state.config.timing.loop) {
            finalProps.repeat = -1;
            finalProps.repeatDelay = state.config.timing.loopDelay;
            finalProps.yoyo = state.config.timing.yoyo;
        }
        
        timeline.to(element, finalProps);
        
        // console.log(`🎬 BasicAnimations to final${state.hasParallax ? ' (parallax-aware)' : ''}`);
        return timeline;
    }
    
    /**
     * ✅ FIXED: animateToInitial with parallax awareness + BLUR FIX
     */
    animateToInitial(element, state) {
        state.isAnimating = true;
        state.isScrubMode = false;
        
        // ✅ CRITICAL: Exclude position properties if parallax is active
        const animationProps = { ...state.initialProps };
        if (state.hasParallax) {
            delete animationProps.x;
            delete animationProps.y;
        }
        
        const timeline = gsap.timeline({
            onComplete: () => {
                state.isAnimating = false;
                state.currentProgress = 0;
                state.targetProgress = 0;
                state.needsUpdate = false;
            }
        });
        
        const initialProps = {
            ...animationProps,
            duration: state.config.timing.duration * 0.5,
            ease: state.config.timing.ease
        };
        
        timeline.to(element, initialProps);
        
        // console.log(`🔄 BasicAnimations to initial${state.hasParallax ? ' (parallax-aware)' : ''}`);
        return timeline;
    }
    
    interpolateProps(initialProps, finalProps, progress) {
        const interpolated = {};
        
        const allProps = new Set([
            ...Object.keys(initialProps),
            ...Object.keys(finalProps)
        ]);
        
        allProps.forEach(prop => {
            const initialValue = this.getPropertyValue(initialProps, prop);
            const finalValue = this.getPropertyValue(finalProps, prop);
            
            interpolated[prop] = this.interpolateProperty(prop, initialValue, finalValue, progress);
        });
        
        return interpolated;
    }
    
    getPropertyValue(props, prop) {
        if (props[prop] !== undefined) {
            return props[prop];
        }
        return this.propertyDefaults[prop] || 0;
    }
    
    interpolateProperty(prop, initialValue, finalValue, progress) {
        switch (prop) {
            case 'backgroundColor':
            case 'textColor':
            case 'color':
                return this.interpolateColor(initialValue, finalValue, progress);
                
            case 'filter':
                return this.interpolateFilter(initialValue, finalValue, progress);
                
            case 'clipPath':
                return this.interpolateClipPath(initialValue, finalValue, progress);
                
            default:
                if (typeof initialValue === 'number' && typeof finalValue === 'number') {
                    return this.lerp(initialValue, finalValue, progress);
                }
                return progress > 0.5 ? finalValue : initialValue;
        }
    }
    
    interpolateColor(initial, final, progress) {
        if (!initial && !final) return 'transparent';
        if (!initial) initial = 'transparent';
        if (!final) final = 'transparent';
        
        if (initial === final) return initial;
        return progress > 0.5 ? final : initial;
    }
    
    /**
     * ✅ BLUR FIX: Smooth filter interpolation without 'none' values
     */
    interpolateFilter(initial, final, progress) {
        const initialBlur = this.extractBlurValue(initial);
        const finalBlur = this.extractBlurValue(final);
        
        if (initialBlur !== null && finalBlur !== null) {
            const interpolatedBlur = this.lerp(initialBlur, finalBlur, progress);
            
            // ❌ OLD CODE: Caused jumps to 'none'
            // return interpolatedBlur > 0 ? `blur(${interpolatedBlur}px)` : 'none';
            
            // ✅ BLUR FIX: Always return blur(Xpx) format for smooth animation
            return `blur(${interpolatedBlur}px)`; // Even blur(0px) animates smoothly!
        }
        
        return progress > 0.5 ? final : initial;
    }
    
    /**
     * ✅ BLUR FIX: Enhanced blur value extraction
     */
    extractBlurValue(filterString) {
        if (!filterString) return 0; // Empty = blur(0px)
        if (filterString === 'none') return 0; // 'none' = blur(0px)
        
        const match = filterString.match(/blur\((\d+(?:\.\d+)?)px\)/);
        
        // ❌ OLD: return match ? parseFloat(match[1]) : null;
        // ✅ NEW: Always return a number for consistent interpolation
        return match ? parseFloat(match[1]) : 0;
    }
    
    interpolateClipPath(initial, final, progress) {
        if (initial === final) return initial;
        
        if (initial && final && initial.includes('{progress}') && final.includes('{progress}')) {
            const pattern = initial.replace(/{progress}/g, '{{PROGRESS}}').replace(/{inverse}/g, '{{INVERSE}}');
            const interpolatedProgress = this.lerp(0, 100, progress);
            const interpolatedInverse = 100 - interpolatedProgress;
            
            return pattern
                .replace(/{{PROGRESS}}/g, interpolatedProgress)
                .replace(/{{INVERSE}}/g, interpolatedInverse);
        }
        
        return progress > 0.5 ? final : initial;
    }
    
    /**
     * ✅ FIXED: Reset with parallax awareness
     */
    reset(element, state) {
        state.currentProgress = 0;
        state.targetProgress = 0;
        state.needsUpdate = true;
        state.isAnimating = false;
        state.isScrubMode = false;
        
        // ✅ CRITICAL: Only reset non-position properties if parallax is active
        const resetProps = { ...state.initialProps };
        if (state.hasParallax) {
            delete resetProps.x;
            delete resetProps.y;
        }
        
        if (Object.keys(resetProps).length > 0) {
            gsap.set(element, resetProps);
        }
        
        // console.log(`🔄 BasicAnimations reset${state.hasParallax ? ' (parallax-aware)' : ''}`);
    }
    
    cleanup(state) {
        if (state.isAnimating) {
            gsap.killTweensOf(state.element);
        }
        
        state.initialProps = null;
        state.finalProps = null;
        state.config = null;
        
        // console.log(`🗑️ BasicAnimations cleaned up`);
    }
}